import React, { useEffect, useMemo, useRef, useState } from "react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function safeParse(json) { try { return JSON.parse(json); } catch { return null; } }

function readPersisted(storageKey) {
  try { const raw = storageKey ? localStorage.getItem(storageKey) : null; return raw ? safeParse(raw) : null; } catch { return null; }
}

const BINDER_CODE = "7864512763";

export default function RoadmapBacklogPuzzle({ storageKey, onSuccess, onAttempt }) {
  const baseItems = useMemo(() => [
    {
      id: "i1", rank: 1,
      title: "Draft — INFRA PLAN 2016",
      meta: "Doc interne (PDF) — v0.9",
      date: "18-02-2016",
      clues: ["Tag: v0.9", "Précède la validation (sinon pas de prod)."],
    },
    {
      id: "i2", rank: 2,
      title: "Validation architecture (2016)",
      meta: "Compte-rendu — 'OK pour prod'",
      commit: "c6f1a2",
      clues: ["Mention: 'après v0.9'", "PR #101"],
    },
    {
      id: "i3", rank: 3,
      title: "Mise en service 'Alpha/Beta/Gamma/Tango'",
      meta: "Déploiement initial",
      date: "05-03-2016",
      clues: ["'Suite directe de PR #101'", "Tag: v1.0"],
    },
    {
      id: "i4", rank: 4,
      title: "MIG-001 — Début de migration vers 'nouvelle infra'",
      meta: "Ticket migration",
      date: "12-06-2019",
      clues: ["Premier ticket MIG", "Vient après le déploiement initial"],
    },
    // i5 et i6 supprimés (switch cœur / décommission — trop obscurs)
    {
      id: "i7", rank: 5,
      title: "Recâblage complet — racks 1/2/principal/4",
      meta: "Fenêtre maintenance",
      date: "27-04-2025",
      clues: ["Ticket #427", "'après la migration MIG-001'"],
    },
    {
      id: "i8", rank: 6,
      title: "Commit — maj schémas officiels + DOCUMENTATION",
      meta: "'Doc racks'",
      commit: "f427e0",
      clues: ["PR #427 (doc)", "'fait le lendemain du recâblage'"],
    },
    {
      id: "i9", rank: 7,
      title: "Note équipe: 'Plan 2016 obsolète'",
      meta: "Archiver l'ancien classeur",
      commit: "depr16",
      clues: ["'écrit après la doc racks'", "Tag: deprecated"],
    },
    {
      id: "i10", rank: 8,
      title: "Documentation technique",
      meta: "à ne pas égarer",
      date: "28-04-2025",
      clues: ["'après PR #427'", "Dernière tâche"],
    },
  ], []);

  const baseById = useMemo(() => {
    const m = new Map();
    baseItems.forEach((it) => m.set(it.id, it));
    return m;
  }, [baseItems]);

  const initialState = useMemo(() => {
    const parsed = readPersisted(storageKey);
    if (parsed?.order?.length) {
      const reconstructed = parsed.order.map((id) => baseById.get(id)).filter(Boolean);
      const missing = baseItems.filter((it) => !parsed.order.includes(it.id));
      return { items: [...reconstructed, ...missing], solved: !!parsed.solved, code: parsed?.code || "" };
    }
    return { items: shuffle(baseItems), solved: false, code: "" };
  }, [storageKey, baseItems, baseById]);

  const [items, setItems] = useState(initialState.items);
  const [solved, setSolved] = useState(initialState.solved);
  const [dragId, setDragId] = useState(null);
  const [feedback, setFeedback] = useState(() => {
    if (initialState?.solved) {
      const code = initialState?.code || BINDER_CODE;
      return `Chronologie reconstituée. Conclusion : le plan d'infra 2016 est obsolète. Code du classeur : ${code}`;
    }
    return "";
  });
  const [checked, setChecked] = useState(false);
  const [posOkById, setPosOkById] = useState({});

  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify({ solved, order: items.map((it) => it.id), code: solved ? BINDER_CODE : undefined }));
  }, [items, solved, storageKey]);

  useEffect(() => {
    const parsed = readPersisted(storageKey);
    if (parsed?.solved) {
      const code = parsed?.code || BINDER_CODE;
      setSolved(true);
      setFeedback(`✅ Chronologie reconstituée. Conclusion : le plan d'infra 2016 est obsolète. Code du classeur : ${code}`);
    }
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetChecks() {
    if (checked) setChecked(false);
    if (Object.keys(posOkById).length) setPosOkById({});
    if (feedback) setFeedback("");
  }

  function handleDragStart(id) { if (solved) return; setDragId(id); resetChecks(); }
  function handleDragEnd() { setDragId(null); }

  function onDropOn(targetId) {
    if (!dragId || dragId === targetId || solved) return;
    setItems((prev) => {
      const a = prev.findIndex((x) => x.id === dragId);
      const b = prev.findIndex((x) => x.id === targetId);
      if (a === -1 || b === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(a, 1);
      next.splice(b, 0, moved);
      return next;
    });
    setDragId(null);
    resetChecks();
  }

  function checkOrder() {
    setFeedback("");
    const byId = {};
    items.forEach((it, idx) => { byId[it.id] = it.rank === idx + 1; });
    setChecked(true);
    setPosOkById(byId);
    const ok = items.every((it, idx) => it.rank === idx + 1);
    onAttempt?.({ ok });
    if (!ok) { setFeedback("L'ordre ne semble pas être le bon…"); return; }
    setSolved(true);
    setFeedback(`✅ Chronologie reconstituée. Conclusion : le plan d'infra 2016 est obsolète. Code du classeur : ${BINDER_CODE}`);
    onSuccess?.({ code: BINDER_CODE });
  }

  function reshuffle() {
    setItems(shuffle(baseItems)); setSolved(false); setDragId(null);
    setFeedback(""); setChecked(false); setPosOkById({});
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <p className="mt-1 text-xs text-slate-300">
          Une suite de tickets et documents mélangés. Remets-les dans l'ordre chronologique.
        </p>
        <p className="mt-2 text-[11px] text-slate-500">Drag & Drop les éléments, puis clique sur "Vérifier".</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-2 pr-2">
        {items.map((it) => {
          const hasCheck = checked && !solved;
          const ok = posOkById?.[it.id];
          const borderClass = solved ? "border-emerald-400/30" : hasCheck ? (ok ? "border-emerald-400/60 ring-1 ring-emerald-400/30" : "border-rose-400/55 ring-1 ring-rose-400/25") : "border-white/10";

          return (
            <div key={it.id}
              draggable={!solved}
              onDragStart={() => handleDragStart(it.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropOn(it.id)}
              className={["select-none rounded-2xl border p-3 transition relative bg-black/40", borderClass, solved ? "opacity-80 cursor-default" : "cursor-grab active:cursor-grabbing hover:border-white/20", dragId === it.id && !solved ? "ring-2 ring-indigo-400/40" : ""].join(" ")}
            >
              <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-slate-800 border border-white/20 flex items-center justify-center text-[11px] font-bold text-slate-300">
                {items.indexOf(it) + 1}
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-100 font-semibold">{it.title}</p>
                  <p className="mt-1 text-xs text-slate-300">{it.meta}</p>
                  {it.clues?.length > 0 && (
                    <ul className="mt-2 text-[11px] text-slate-400 list-disc pl-5 space-y-1">
                      {it.clues.map((c, idx) => (<li key={idx}>{c}</li>))}
                    </ul>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  {it.date && <p className="text-[11px] font-mono text-amber-200">{it.date}</p>}
                  {it.commit && <p className="text-[11px] font-mono text-cyan-200">commit {it.commit}</p>}
                  {!it.date && !it.commit && <p className="text-[11px] text-slate-500">—</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {feedback && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3">
          <p className="text-sm font-semibold text-emerald-200">{feedback}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={checkOrder} disabled={solved}
          className={["rounded-xl px-4 py-2 text-xs font-semibold transition", solved ? "bg-emerald-700/40 text-emerald-100 cursor-default" : "bg-indigo-600 hover:bg-indigo-500"].join(" ")}>
          Vérifier
        </button>
        <button type="button" onClick={reshuffle}
          className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 text-xs font-semibold transition">
          Re-mélanger
        </button>
      </div>
    </div>
  );
}